import React, { useState, useEffect, useCallback, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSync,
  faDesktop,
  faUsers,
  faClock,
  faChartLine,
  faPlay,
  faPause,
  faTimes,
  faChevronLeft,
  faChevronRight,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import "../../css/Common.css";
import "./css/UserSessionsAdmin.css";
import AdminDashboardAPI from "../../services/AdminDashboardAPI";
import LoadingSpinner from "../../common/LoadingSpinner";
import toast from "react-hot-toast";
import { useTranslation } from "../../../../i18n/LanguageContext";

const RESOLUTION_OPTIONS = [
  { value: 1, label: "1 min" },
  { value: 5, label: "5 min" },
  { value: 15, label: "15 min" },
  { value: 30, label: "30 min" },
  { value: 60, label: "1 hour" },
];

const HOURS_OPTIONS = [
  { value: 1, label: "1 hour" },
  { value: 4, label: "4 hours" },
  { value: 8, label: "8 hours" },
  { value: 24, label: "24 hours" },
];

const REFRESH_INTERVAL_MS = 30000; // 30 seconds

function UserSessionsAdmin() {
  const { t } = useTranslation();
  const [sessionData, setSessionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resolution, setResolution] = useState(5);
  const [hoursBack, setHoursBack] = useState(4);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const [showMetric, setShowMetric] = useState("estimatedSessions"); // or "logins"
  const intervalRef = useRef(null);
  const svgRef = useRef(null);

  // ── Active Users side sheet ──
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetUsers, setSheetUsers] = useState([]);
  const [sheetPage, setSheetPage] = useState(0);
  const [sheetPageSize, setSheetPageSize] = useState(20);
  const [sheetTotal, setSheetTotal] = useState(0);
  const [sheetTotalPages, setSheetTotalPages] = useState(0);
  const [sheetLoading, setSheetLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const data = await AdminDashboardAPI.getUserSessions(
        resolution,
        hoursBack,
      );
      setSessionData(data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error("[UserSessions] Error fetching session data:", error);
      toast.error(t("userSessions.toast.sessionLoadFailed"));
    } finally {
      setLoading(false);
    }
  }, [resolution, hoursBack]);

  // Initial load
  useEffect(() => {
    setLoading(true);
    fetchData();
  }, [fetchData]);

  // Auto-refresh
  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(fetchData, REFRESH_INTERVAL_MS);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [autoRefresh, fetchData]);

  const handleManualRefresh = () => {
    setLoading(true);
    fetchData();
  };

  const fetchActiveUsers = useCallback(async (page, pageSize) => {
    setSheetLoading(true);
    try {
      const data = await AdminDashboardAPI.getActiveUsers(page, pageSize);
      setSheetUsers(data.users ?? []);
      setSheetTotal(data.totalElements ?? 0);
      setSheetTotalPages(data.totalPages ?? 0);
    } catch (error) {
      console.error("[UserSessions] Error fetching active users:", error);
      toast.error(t("userSessions.toast.activeUsersLoadFailed"));
    } finally {
      setSheetLoading(false);
    }
  }, []);

  const openActiveUsersSheet = () => {
    setSheetPage(0);
    setSheetOpen(true);
    fetchActiveUsers(0, sheetPageSize);
  };

  const handleSheetPageChange = (newPage) => {
    setSheetPage(newPage);
    fetchActiveUsers(newPage, sheetPageSize);
  };

  const handleSheetPageSizeChange = (newSize) => {
    setSheetPageSize(newSize);
    setSheetPage(0);
    fetchActiveUsers(0, newSize);
  };

  // ── Chart rendering ──
  const renderChart = () => {
    if (!sessionData?.timeSeries || sessionData.timeSeries.length === 0) {
      return (
        <div className="sessions-chart-empty">
          <FontAwesomeIcon icon={faChartLine} />
          <p>No session data available for the selected time range.</p>
        </div>
      );
    }

    const series = sessionData.timeSeries;
    const values = series.map((b) =>
      showMetric === "estimatedSessions" ? b.estimatedSessions : b.logins,
    );
    const maxVal = Math.max(...values, 1);

    // Chart dimensions
    const width = 900;
    const height = 320;
    const padLeft = 55;
    const padRight = 20;
    const padTop = 20;
    const padBottom = 50;
    const chartW = width - padLeft - padRight;
    const chartH = height - padTop - padBottom;

    // Scale helpers
    const xScale = (i) => padLeft + (i / (series.length - 1 || 1)) * chartW;
    const yScale = (v) => padTop + chartH - (v / maxVal) * chartH;

    // Build polyline points
    const points = values
      .map((v, i) => `${xScale(i).toFixed(1)},${yScale(v).toFixed(1)}`)
      .join(" ");

    // Area fill path
    const areaPath =
      `M ${xScale(0)},${yScale(0)} ` +
      values
        .map((v, i) => `L ${xScale(i).toFixed(1)},${yScale(v).toFixed(1)}`)
        .join(" ") +
      ` L ${xScale(values.length - 1)},${padTop + chartH} L ${padLeft},${padTop + chartH} Z`;

    // Y-axis gridlines
    const gridLines = 5;
    const yGrids = [];
    for (let i = 0; i <= gridLines; i++) {
      const val = Math.round((maxVal / gridLines) * i);
      const y = yScale(val);
      yGrids.push({ val, y });
    }

    // X-axis labels (show ~8 labels max)
    const labelInterval = Math.max(1, Math.floor(series.length / 8));
    const xLabels = series
      .map((b, i) => {
        if (i % labelInterval !== 0 && i !== series.length - 1) return null;
        const dt = new Date(b.timestamp);
        const hr = dt.getHours().toString().padStart(2, "0");
        const mn = dt.getMinutes().toString().padStart(2, "0");
        return { i, label: `${hr}:${mn}`, x: xScale(i) };
      })
      .filter(Boolean);

    return (
      <div className="sessions-chart-wrapper">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${width} ${height}`}
          className="sessions-chart-svg"
          onMouseLeave={() => setHoveredPoint(null)}
        >
          {/* Grid lines */}
          {yGrids.map((g, idx) => (
            <g key={idx}>
              <line
                x1={padLeft}
                y1={g.y}
                x2={width - padRight}
                y2={g.y}
                stroke="#e5e7eb"
                strokeWidth="1"
              />
              <text
                x={padLeft - 8}
                y={g.y + 4}
                textAnchor="end"
                fontSize="11"
                fill="#6b7280"
              >
                {g.val}
              </text>
            </g>
          ))}

          {/* Area fill */}
          <path d={areaPath} fill="url(#sessionGradient)" opacity="0.3" />

          {/* Gradient definition */}
          <defs>
            <linearGradient
              id="sessionGradient"
              x1="0%"
              y1="0%"
              x2="0%"
              y2="100%"
            >
              <stop
                offset="0%"
                stopColor={
                  showMetric === "estimatedSessions" ? "#3b82f6" : "#10b981"
                }
                stopOpacity="0.4"
              />
              <stop
                offset="100%"
                stopColor={
                  showMetric === "estimatedSessions" ? "#3b82f6" : "#10b981"
                }
                stopOpacity="0.02"
              />
            </linearGradient>
          </defs>

          {/* Line */}
          <polyline
            points={points}
            fill="none"
            stroke={showMetric === "estimatedSessions" ? "#3b82f6" : "#10b981"}
            strokeWidth="2.5"
            strokeLinejoin="round"
            strokeLinecap="round"
          />

          {/* Data points (interactive) */}
          {values.map((v, i) => (
            <circle
              key={i}
              cx={xScale(i)}
              cy={yScale(v)}
              r={hoveredPoint === i ? 6 : 3}
              fill={
                hoveredPoint === i
                  ? showMetric === "estimatedSessions"
                    ? "#2563eb"
                    : "#059669"
                  : showMetric === "estimatedSessions"
                    ? "#3b82f6"
                    : "#10b981"
              }
              stroke="white"
              strokeWidth={hoveredPoint === i ? 2 : 1}
              style={{ cursor: "pointer" }}
              onMouseEnter={() => setHoveredPoint(i)}
            />
          ))}

          {/* X-axis labels */}
          {xLabels.map((l) => (
            <text
              key={l.i}
              x={l.x}
              y={height - 10}
              textAnchor="middle"
              fontSize="11"
              fill="#6b7280"
            >
              {l.label}
            </text>
          ))}

          {/* Hover tooltip */}
          {hoveredPoint !== null && series[hoveredPoint] && (
            <g>
              {/* Vertical guide line */}
              <line
                x1={xScale(hoveredPoint)}
                y1={padTop}
                x2={xScale(hoveredPoint)}
                y2={padTop + chartH}
                stroke="#9ca3af"
                strokeWidth="1"
                strokeDasharray="4 3"
              />
              {/* Tooltip box */}
              <rect
                x={Math.min(xScale(hoveredPoint) + 10, width - 165)}
                y={Math.max(yScale(values[hoveredPoint]) - 45, padTop)}
                width="155"
                height="40"
                rx="6"
                fill="white"
                stroke="#d1d5db"
                strokeWidth="1"
                filter="drop-shadow(0 1px 3px rgba(0,0,0,0.1))"
              />
              <text
                x={Math.min(xScale(hoveredPoint) + 18, width - 157)}
                y={Math.max(yScale(values[hoveredPoint]) - 27, padTop + 18)}
                fontSize="11"
                fill="#374151"
                fontWeight="600"
              >
                {(() => {
                  const dt = new Date(series[hoveredPoint].timestamp);
                  return dt.toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  });
                })()}
              </text>
              <text
                x={Math.min(xScale(hoveredPoint) + 18, width - 157)}
                y={Math.max(yScale(values[hoveredPoint]) - 12, padTop + 33)}
                fontSize="11"
                fill="#6b7280"
              >
                {showMetric === "estimatedSessions"
                  ? `Sessions: ${values[hoveredPoint]}`
                  : `Logins: ${values[hoveredPoint]}`}
              </text>
            </g>
          )}
        </svg>
      </div>
    );
  };

  if (loading && !sessionData) {
    return (
      <div className="user-sessions-admin">
        <LoadingSpinner message="Loading session data..." />
      </div>
    );
  }

  return (
    <div className="user-sessions-admin">
      {/* Header */}
      <div className="sessions-header">
        <h3>
          <FontAwesomeIcon icon={faDesktop} /> User Sessions
        </h3>
        <div className="sessions-header-controls">
          <button
            className={`sessions-auto-refresh-btn ${autoRefresh ? "active" : ""}`}
            onClick={() => setAutoRefresh(!autoRefresh)}
            title={autoRefresh ? "Pause live updates" : "Resume live updates"}
          >
            <FontAwesomeIcon icon={autoRefresh ? faPause : faPlay} />
            {autoRefresh ? " Live" : " Paused"}
          </button>
          <button
            className="sessions-refresh-btn"
            onClick={handleManualRefresh}
            disabled={loading}
          >
            <FontAwesomeIcon icon={faSync} spin={loading} /> Refresh
          </button>
          {lastUpdated && (
            <span className="sessions-last-updated">
              Updated:{" "}
              {lastUpdated.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
              })}
            </span>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="sessions-kpi-grid">
        <div className="sessions-kpi-card active-sessions">
          <FontAwesomeIcon icon={faDesktop} className="kpi-icon" />
          <div className="kpi-value">{sessionData?.activeSessions ?? 0}</div>
          <div className="kpi-label">Active Sessions</div>
          <div className="kpi-sublabel">
            {sessionData?.redisAvailable === false
              ? "Estimated from DB (Redis unavailable)"
              : "Currently in Redis"}
          </div>
        </div>
        <div
          className="sessions-kpi-card active-users clickable"
          onClick={openActiveUsersSheet}
          title="Click to view active users"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && openActiveUsersSheet()}
        >
          <FontAwesomeIcon icon={faUsers} className="kpi-icon" />
          <div className="kpi-value">{sessionData?.activeUsers ?? 0}</div>
          <div className="kpi-label">Active Users</div>
          <div className="kpi-sublabel">
            {sessionData?.redisAvailable === false
              ? "Estimated from DB (Redis unavailable)"
              : "Distinct users online"}
          </div>
          <div className="kpi-drill-hint">Click to view details</div>
        </div>
        <div className="sessions-kpi-card peak-sessions">
          <FontAwesomeIcon icon={faChartLine} className="kpi-icon" />
          <div className="kpi-value">
            {sessionData?.timeSeries
              ? Math.max(
                  ...sessionData.timeSeries.map((b) => b.estimatedSessions),
                )
              : 0}
          </div>
          <div className="kpi-label">Peak Sessions</div>
          <div className="kpi-sublabel">
            Last {hoursBack}h ({resolution}m buckets)
          </div>
        </div>
        <div className="sessions-kpi-card total-logins">
          <FontAwesomeIcon icon={faClock} className="kpi-icon" />
          <div className="kpi-value">
            {sessionData?.timeSeries
              ? sessionData.timeSeries.reduce((s, b) => s + b.logins, 0)
              : 0}
          </div>
          <div className="kpi-label">Total Logins</div>
          <div className="kpi-sublabel">Last {hoursBack}h</div>
        </div>
      </div>

      {/* Controls */}
      <div className="sessions-controls-row">
        <div className="sessions-control-group">
          <label>Resolution</label>
          <select
            value={resolution}
            onChange={(e) => setResolution(Number(e.target.value))}
          >
            {RESOLUTION_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className="sessions-control-group">
          <label>Time Range</label>
          <select
            value={hoursBack}
            onChange={(e) => setHoursBack(Number(e.target.value))}
          >
            {HOURS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className="sessions-control-group">
          <label>Metric</label>
          <div className="sessions-metric-toggle">
            <button
              className={showMetric === "estimatedSessions" ? "active" : ""}
              onClick={() => setShowMetric("estimatedSessions")}
            >
              Sessions
            </button>
            <button
              className={showMetric === "logins" ? "active" : ""}
              onClick={() => setShowMetric("logins")}
            >
              Logins
            </button>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="sessions-chart-container">
        <h4>
          {showMetric === "estimatedSessions"
            ? "Estimated Active Sessions"
            : "Login Events"}{" "}
          — Last {hoursBack}h ({resolution}-min resolution)
        </h4>
        {renderChart()}
      </div>

      {/* Active Users Side Sheet */}
      {sheetOpen && (
        <>
          <div className="sheet-overlay" onClick={() => setSheetOpen(false)} />
          <div className="active-users-sheet">
            <div className="sheet-header">
              <div className="sheet-title">
                <FontAwesomeIcon icon={faUsers} />
                <span>Active Users</span>
                <span className="sheet-total-badge">{sheetTotal}</span>
              </div>
              <button
                className="sheet-close-btn"
                onClick={() => setSheetOpen(false)}
                aria-label="Close"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {/* Page-size selector */}
            <div className="sheet-controls">
              <span className="sheet-controls-label">Show per page:</span>
              {[20, 50, 100].map((size) => (
                <button
                  key={size}
                  className={`sheet-page-size-btn ${
                    sheetPageSize === size ? "active" : ""
                  }`}
                  onClick={() => handleSheetPageSizeChange(size)}
                >
                  {size}
                </button>
              ))}
            </div>

            {/* User list */}
            <div className="sheet-body">
              {sheetLoading ? (
                <div className="sheet-loading">
                  <FontAwesomeIcon icon={faSync} spin /> Loading users…
                </div>
              ) : sheetUsers.length === 0 ? (
                <div className="sheet-empty">
                  <FontAwesomeIcon icon={faUser} />
                  <p>No active users found.</p>
                </div>
              ) : (
                <table className="sheet-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Last Login</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sheetUsers.map((u, idx) => (
                      <tr key={u.userId ?? idx}>
                        <td className="sheet-row-num">
                          {sheetPage * sheetPageSize + idx + 1}
                        </td>
                        <td>
                          {u.firstName || u.lastName ? (
                            `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim()
                          ) : (
                            <span className="sheet-unknown">—</span>
                          )}
                        </td>
                        <td className="sheet-email">{u.email ?? "—"}</td>
                        <td className="sheet-login-time">
                          {u.lastLoginAt
                            ? new Date(u.lastLoginAt).toLocaleString([], {
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                            : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination */}
            {sheetTotalPages > 1 && (
              <div className="sheet-pagination">
                <button
                  className="sheet-pager-btn"
                  onClick={() => handleSheetPageChange(sheetPage - 1)}
                  disabled={sheetPage === 0}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                <span className="sheet-page-info">
                  Page {sheetPage + 1} of {sheetTotalPages}
                </span>
                <button
                  className="sheet-pager-btn"
                  onClick={() => handleSheetPageChange(sheetPage + 1)}
                  disabled={sheetPage >= sheetTotalPages - 1}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default UserSessionsAdmin;
